/**
 * Data Schemas — Runtime validation for QuestionData payloads.
 *
 * PEDAGOGICAL_CONTRACT_SPEC §3.2 — Plain TypeScript validators (no Zod dependency).
 * These validate that question.data contains the required structured fields
 * for each question type, used by questionLoader.ts at load-time.
 *
 * Design:
 * - Each validator returns { valid: true } or { valid: false, errors: string[] }
 * - Validators are permissive (passthrough unknown fields) for backwards compat
 * - Only validates fields that MUST exist for the renderer to work correctly
 */

import type { QuestionData, QuestionType } from '../types';
export interface ValidationResult {
  valid: boolean;
  errors: string[];
}
const OK: ValidationResult = {
  valid: true,
  errors: []
};
function fail(...errors: string[]): ValidationResult {
  return {
    valid: false,
    errors
  };
}

// ─── Type-specific validators ─────────────────────────────────────────────────

function validateColumnArithmetic(data: QuestionData): ValidationResult {
  const errors: string[] = [];
  const ca = data.columnArithmetic;
  if (!ca) {
    // FG4 §Fase C / S9 — Named partial-shape exemption.
    // The renderer has a regex-based fallback for seeds that pre-date
    // the `data.columnArithmetic` payload contract. Closing this exemption
    // requires a seed migration (REK-CIJF-* families), tracked under §S10
    // alongside the equivalence-class work. Documented here so the
    // permissiveness is observable rather than anonymous.
    return OK;
  }
  if (typeof ca !== 'object') {
    return fail('data.columnArithmetic must be an object');
  }
  const obj = ca as Record<string, unknown>;
  if (typeof obj.operand1 !== 'number') {
    errors.push('data.columnArithmetic.operand1 must be a number');
  }
  if (typeof obj.operand2 !== 'number') {
    errors.push('data.columnArithmetic.operand2 must be a number');
  }
  if (obj.operator !== undefined && !['+', '-', '×'].includes(obj.operator as string)) {
    errors.push('data.columnArithmetic.operator must be "+", "-", or "×"');
  }
  return errors.length > 0 ? {
    valid: false,
    errors
  } : OK;
}
function validateLongDivision(data: QuestionData): ValidationResult {
  const errors: string[] = [];
  const ld = data.longDivision;
  if (!ld) {
    // FG4 §Fase C / S9 — Named partial-shape exemption (mirror of the
    // columnArithmetic case). Same rationale: legacy seeds without the
    // `data.longDivision` payload rely on the renderer's regex fallback.
    // Closing this requires a seed migration tracked under §S10.
    return OK;
  }
  if (typeof ld !== 'object') {
    return fail('data.longDivision must be an object');
  }
  const obj = ld as Record<string, unknown>;
  if (typeof obj.dividend !== 'number') {
    errors.push('data.longDivision.dividend must be a number');
  }
  if (typeof obj.divisor !== 'number') {
    errors.push('data.longDivision.divisor must be a number');
  }
  if (typeof obj.divisor === 'number' && obj.divisor === 0) {
    errors.push('data.longDivision.divisor must not be zero');
  }
  return errors.length > 0 ? {
    valid: false,
    errors
  } : OK;
}
function validateClock(data: QuestionData): ValidationResult {
  if (!data.clock) return fail('data.clock is required for clock questions');
  const errors: string[] = [];
  const c = data.clock;
  if (!c.mode) errors.push('data.clock.mode is required');
  if (!c.target) errors.push('data.clock.target is required');
  if (c.target) {
    if (typeof c.target.hours !== 'number' || c.target.hours < 0 || c.target.hours > 23) {
      errors.push('data.clock.target.hours must be 0-23');
    }
    if (typeof c.target.minutes !== 'number' || c.target.minutes < 0 || c.target.minutes > 59) {
      errors.push('data.clock.target.minutes must be 0-59');
    }
  }
  return errors.length > 0 ? {
    valid: false,
    errors
  } : OK;
}
function validateMoney(data: QuestionData): ValidationResult {
  if (!data.money) return fail('data.money is required for money questions');
  const errors: string[] = [];
  const m = data.money;
  if (!m.mode) errors.push('data.money.mode is required');
  if (typeof m.targetCents !== 'number') errors.push('data.money.targetCents is required');
  return errors.length > 0 ? {
    valid: false,
    errors
  } : OK;
}
function validateFillIn(data: QuestionData): ValidationResult {
  if (!data.acceptedAnswers || !Array.isArray(data.acceptedAnswers)) {
    return fail('data.acceptedAnswers is required for fill_in questions');
  }
  if (data.acceptedAnswers.length === 0) {
    return fail('data.acceptedAnswers must have at least one answer');
  }
  return OK;
}
function validateGridShade(data: QuestionData): ValidationResult {
  if (!data.gridShade) return fail('data.gridShade is required for grid_shade questions');
  return OK;
}
function validateStepSolver(data: QuestionData): ValidationResult {
  if (!data.stepSolver) return fail('data.stepSolver is required for step_solver questions');
  return OK;
}
function validateSentenceParser(data: QuestionData): ValidationResult {
  if (!data.sentenceParser) return fail('data.sentenceParser is required for sentence_parser questions');
  return OK;
}
function validateComparisonJudge(data: QuestionData): ValidationResult {
  if (!data.comparisonJudge) return fail('data.comparisonJudge is required for comparison_judge questions');
  return OK;
}
function validateMultiplicationGrid(data: QuestionData): ValidationResult {
  if (!data.hiddenCells && !data.multiplicationGrid) return fail('data.hiddenCells or data.multiplicationGrid is required for multiplication_grid questions');
  return OK;
}
function validateEstimationSlider(data: QuestionData): ValidationResult {
  const d = data.estimationSlider || data;
  if (typeof d.min !== 'number' && typeof d.minValue !== 'number') return fail('data.min or data.minValue is required for estimation_slider questions');
  if (typeof d.max !== 'number' && typeof d.maxValue !== 'number') return fail('data.max or data.maxValue is required for estimation_slider questions');
  return OK;
}
function validateThermometerSlider(data: QuestionData): ValidationResult {
  const d = data.thermometerSlider || data;
  if (typeof d.minTemp !== 'number') return fail('data.minTemp is required for thermometer_slider questions');
  if (typeof d.maxTemp !== 'number') return fail('data.maxTemp is required for thermometer_slider questions');
  return OK;
}
function validateVerbConjugationFlow(data: QuestionData): ValidationResult {
  const d = data.verbConjugationFlow || data;
  if (!d.verb && !d.targetForm) return fail('data.verb or data.targetForm is required for verb_conjugation_flow questions');
  if (!d.steps || !Array.isArray(d.steps)) return fail('data.steps array is required for verb_conjugation_flow questions');
  return OK;
}
function validateSentenceErrorJudge(data: QuestionData): ValidationResult {
  const d = data.sentenceErrorJudge || data;
  if (!d.sentences || !Array.isArray(d.sentences)) return fail('data.sentences array is required for sentence_error_judge questions');
  if (d.sentences.length < 2) return fail('data.sentences must have at least 2 sentences');
  return OK;
}
function validateSquareRootBuilder(data: QuestionData): ValidationResult {
  const c = data.squareRootBuilder;
  if (!c) return fail('data.squareRootBuilder is required for square_root_builder questions');
  if (typeof c.targetValue !== 'number' || c.targetValue <= 0) return fail('data.squareRootBuilder.targetValue must be a positive number');
  if (c.maxSize !== undefined && (typeof c.maxSize !== 'number' || c.maxSize < 2)) return fail('data.squareRootBuilder.maxSize must be >= 2 when provided');
  return OK;
}
function validatePlaceValueBuild(data: QuestionData): ValidationResult {
  // Accept both namespaced (data.placeValueBuild) and legacy (data root) shapes
  // for the migration period.
  const c = data.placeValueBuild ?? data as {
    target?: unknown;
  };
  if (!c || typeof (c as {
    target?: unknown;
  }).target !== 'number') return fail('data.placeValueBuild.target (or legacy data.target) must be a non-negative number');
  const target = (c as {
    target: number;
  }).target;
  if (target < 0) return fail('data.placeValueBuild.target must be non-negative');
  return OK;
}
function validateBarModelPart(data: QuestionData): ValidationResult {
  const c = data.barModelPart;
  if (!c) return fail('data.barModelPart is required for bar_model_part questions');
  if (typeof c.total !== 'number' || c.total <= 0) return fail('data.barModelPart.total must be > 0');
  if (typeof c.segments !== 'number' || c.segments < 2) return fail('data.barModelPart.segments must be >= 2');
  if (!Array.isArray(c.highlighted) || c.highlighted.length === 0) return fail('data.barModelPart.highlighted must be a non-empty array');
  if (c.highlighted.some(i => typeof i !== 'number' || i < 0 || i >= (c.segments as number))) return fail('data.barModelPart.highlighted indices must be 0-based and < segments');
  return OK;
}
function validateScientificNotationBuilder(data: QuestionData): ValidationResult {
  const c = data.scientificNotationBuilder;
  if (!c) return fail('data.scientificNotationBuilder is required for scientific_notation_builder questions');
  if (typeof c.targetValue !== 'number' || !Number.isFinite(c.targetValue)) return fail('data.scientificNotationBuilder.targetValue must be a finite number');
  if (c.targetValue <= 0) return fail('data.scientificNotationBuilder.targetValue must be > 0 (wetenschappelijke notatie expects a positive number)');
  const minE = c.minExponent;
  const maxE = c.maxExponent;
  if (minE !== undefined && (typeof minE !== 'number' || !Number.isInteger(minE) || minE < 0)) return fail('data.scientificNotationBuilder.minExponent must be a non-negative integer');
  if (maxE !== undefined && (typeof maxE !== 'number' || !Number.isInteger(maxE) || maxE < 0)) return fail('data.scientificNotationBuilder.maxExponent must be a non-negative integer');
  if (typeof minE === 'number' && typeof maxE === 'number' && minE > maxE) return fail('data.scientificNotationBuilder.minExponent must be ≤ maxExponent');
  return OK;
}
function validateVerbEnding(data: QuestionData): ValidationResult {
  const c = data.verbEnding;
  if (!c) return fail('data.verbEnding is required for verb_ending questions');
  const variants = ['tt-tstam', 'vt-zwak', 'vd-kof', 'custom'];
  if (!c.variant || !variants.includes(c.variant)) return fail('data.verbEnding.variant must be one of "tt-tstam" | "vt-zwak" | "vd-kof" | "custom"');
  if (c.variant === 'custom' && (!c.endings || c.endings.length === 0)) return fail('data.verbEnding.endings is required (non-empty) when variant === "custom"');
  if (!Array.isArray(c.items) || c.items.length === 0) return fail('data.verbEnding.items must be a non-empty array (1–4) of { stem, correct }');
  const errors: string[] = [];
  c.items.forEach((item, idx) => {
    if (!item || typeof item.stem !== 'string' || item.stem.length === 0) errors.push(`data.verbEnding.items[${idx}].stem must be a non-empty string`);
    if (item && typeof item.correct !== 'string') errors.push(`data.verbEnding.items[${idx}].correct must be a string ('' means "no ending")`);
  });
  return errors.length > 0 ? {
    valid: false,
    errors
  } : OK;
}
function validateWordBuilder(data: QuestionData): ValidationResult {
  const c = data.wordBuilder;
  if (!c) return fail('data.wordBuilder is required for word_builder questions');
  const variants = ['ei-ij', 'd-t', 'dt', 'custom'];
  if (!c.variant || !variants.includes(c.variant)) return fail('data.wordBuilder.variant must be one of "ei-ij" | "d-t" | "dt" | "custom"');
  if (!Array.isArray(c.items) || c.items.length === 0) return fail('data.wordBuilder.items must be a non-empty array (1–4) of { clue, word }');
  const errors: string[] = [];
  c.items.forEach((item, idx) => {
    if (!item || typeof item.clue !== 'string' || item.clue.length === 0) errors.push(`data.wordBuilder.items[${idx}].clue must be a non-empty string`);
    if (!item || typeof item.word !== 'string' || item.word.length === 0) errors.push(`data.wordBuilder.items[${idx}].word must be a non-empty string`);
  });
  return errors.length > 0 ? {
    valid: false,
    errors
  } : OK;
}

// ─── Schema Registry ──────────────────────────────────────────────────────────

type DataValidator = (data: QuestionData) => ValidationResult;

/**
 * Map of question types to their data validators.
 *
 * STRICTNESS DEFAULT (FG4 §Fase C, S9): a type is EITHER in this registry
 * (gets a concrete validator) OR in `UNREGISTERED_ALLOWLIST` below (explicit
 * opt-out, with a documented reason). Adding a NEW QuestionType without
 * either of those steps now produces a structured warning at load-time —
 * permissiveness is no longer a silent default.
 *
 * Anti-bandaid principle #3: every exception is named, owned, and visible.
 */
const DATA_VALIDATORS: Partial<Record<QuestionType, DataValidator>> = {
  cijferend_rekenen: validateColumnArithmetic,
  cijferend_delen: validateLongDivision,
  clock: validateClock,
  clock_set: validateClock,
  money: validateMoney,
  fill_in: validateFillIn,
  grid_shade: validateGridShade,
  step_solver: validateStepSolver,
  sentence_parser: validateSentenceParser,
  comparison_judge: validateComparisonJudge,
  multiplication_grid: validateMultiplicationGrid,
  estimation_slider: validateEstimationSlider,
  thermometer_slider: validateThermometerSlider,
  verb_conjugation_flow: validateVerbConjugationFlow,
  sentence_error_judge: validateSentenceErrorJudge,
  square_root_builder: validateSquareRootBuilder,
  place_value_build: validatePlaceValueBuild,
  bar_model_part: validateBarModelPart,
  scientific_notation_builder: validateScientificNotationBuilder,
  verb_ending: validateVerbEnding,
  word_builder: validateWordBuilder
};

/**
 * EXPLICIT allowlist of QuestionTypes that intentionally have NO data-shape
 * validator here (FG4 §Fase C, S9). For each entry the comment documents WHY
 * the type is exempt — typically because the type is validated structurally
 * by the dedicated validators under `validators/` (interactive, language,
 * map, chart) or because the type carries no `data` payload at all (the
 * base Question shape — options/correctAnswer — is enough).
 *
 * This list REPLACES the pre-S9 implicit `return OK` for unregistered types.
 * A new QuestionType is now expected to either:
 *   1. Register a validator in `DATA_VALIDATORS` above, OR
 *   2. Be added here with a one-line justification.
 *
 * Missing both ⇒ `validateQuestionData` emits an `UNREGISTERED_DATA_SCHEMA`
 * warning so the omission is observable instead of silent.
 */
const UNREGISTERED_ALLOWLIST: ReadonlySet<QuestionType> = new Set<QuestionType>([
// Base shape (options + correctAnswer) is structurally sufficient.
'multiple_choice', 'true_false_series',
// Validated by content/questions/validators/interactiveValidators.ts.
'drag_drop_fill', 'matching', 'ordering', 'categorize', 'error_spotting', 'strategy_solver', 'fraction_equivalence_match', 'fraction_strip_builder', 'double_number_line', 'comma_shift',
// Validated by content/questions/validators/chartValidators.ts.
'bar_chart', 'line_chart', 'pie_chart', 'table_reading', 'schedule_reading', 'number_sequence', 'ratio_table',
// Validated by content/questions/validators/languageValidators.ts.
'text_highlight',
// Validated by content/questions/validators/mapValidators.ts.
'map_locate_region', 'map_locate_point', 'map_locate_line', 'map_place_cities']);

/**
 * Validate question data against the schema for its question type.
 *
 * PRE-S9 behaviour: `if (!validator) return OK` — every unregistered type
 * silently passed. Combined with the per-validator `return OK // Permissive
 * during migration` shortcuts (columnArithmetic, longDivision), this was the
 * exact cliff-edge that FG4 Fase C set out to dismantle: permissiveness as a
 * silent default rather than a named, observable exception.
 *
 * POST-S9 behaviour (anti-bandaid #3):
 *   - Type IS registered → run the validator (existing strictness preserved).
 *   - Type is in `UNREGISTERED_ALLOWLIST` → OK (documented, named exemption).
 *   - Type is NEITHER → fail with `UNREGISTERED_DATA_SCHEMA`. The warning is
 *     surfaced by the dev-mode loader in `questionLoader.ts`; production
 *     callers are unchanged because the schema-validator is dev-only today
 *     (no runtime gate consumes it). Strict-additive: no production regression
 *     is possible.
 *
 * NOTE: the two per-validator `return OK // Permissive during migration`
 * shortcuts (validateColumnArithmetic, validateLongDivision) are deliberately
 * preserved — they exempt a missing SUBFIELD (`data.columnArithmetic`),
 * not a whole type, and closing them requires a seed migration that is
 * tracked separately in §S10 (FG2 Fase 2 + content debt).
 */
export function validateQuestionData(questionType: QuestionType, data: QuestionData | undefined): ValidationResult {
  const validator = DATA_VALIDATORS[questionType];
  if (validator) {
    if (!data) return fail(`data is required for question type "${questionType}"`);
    return validator(data);
  }
  if (UNREGISTERED_ALLOWLIST.has(questionType)) {
    return OK;
  }
  return fail(`UNREGISTERED_DATA_SCHEMA: question type "${questionType}" has neither a registered validator in DATA_VALIDATORS nor an explicit entry in UNREGISTERED_ALLOWLIST. Add a validator or document the exemption (FG4 §Fase C / S9).`);
}

/**
 * Check if a question type has a registered data schema.
 */
export function hasDataSchema(questionType: QuestionType): boolean {
  return questionType in DATA_VALIDATORS;
}

/**
 * FG4 §Fase C / S9 — Introspection helper for admin-side audits.
 *
 * Returns the explicit list of QuestionTypes that are exempt from data-shape
 * validation. Used by the admin Settings UI to surface the named exemptions
 * (anti-bandaid principle #3: permissiveness is observable, not hidden).
 */
export function getUnregisteredDataSchemaAllowlist(): readonly QuestionType[] {
  return [...UNREGISTERED_ALLOWLIST];
}